/** @module timeUtils */
import { timeService } from '@bedrock/fido-sdk';

/**
 * Compare Time in SECONDS !
 *
 * @function differenceInSeconds
 * @param {number} timestampEnd the highest time in seconds
 * @param {number} timestampStart the lowest time in seconds
 */
export const differenceInSeconds = (timestampEnd, timestampStart) => timestampEnd - timestampStart;

export const differenceInMinutes = (timestampEnd, timestampStart) => (timestampEnd - timestampStart) / 60;

export const differenceInMs = (timestampEnd, timestampStart) => (timestampEnd - timestampStart) * 1000;

export const getElapsedTime = () => Math.floor(performance.now());

export const toSeconds = msValue => msValue / 1000;

const toMs = valueInSeconds => valueInSeconds * 1000;

const secondsToHours = valueInSeconds => valueInSeconds / 3600;

const secondsToMinutes = valueInSeconds => (valueInSeconds / 60) % 60;

const secondsRemainder = valueInSeconds => valueInSeconds % 60;

export const floorValue = value => Math.floor(value);

export const ceilValue = value => Math.ceil(value);

export const toFixedValue = value => parseFloat(value.toFixed(2));

export const fixedClamp = (value, min, max) => {
  const fixedValue = toFixedValue(value);

  if (fixedValue < min) {
    return min;
  }
  if (fixedValue > max) {
    return max;
  }

  return fixedValue;
};

export const getTimestamp = (serverTime, initialElapsedTime) =>
  floorValue(serverTime + toSeconds(getElapsedTime() - initialElapsedTime));

export const getMsTimestamp = (serverTime, initialElapsedTime) =>
  floorValue(toMs(serverTime) + (getElapsedTime() - initialElapsedTime));

export const get6PlayTime = async () => {
  try {
    const serverTime = await timeService.fetch();
    if (!serverTime || typeof serverTime.timestamp !== 'number') {
      throw new Error('Invalid timestamp');
    }

    return serverTime.timestamp;
  } catch (err) {
    throw new Error(`Unable to retrieve server time: ${err.message}`);
  }
};

export const getPercentageOfTime = ({ timestampStart, timestampEnd, serverTime, initialElapsedTime }) => {
  const currentTime = getTimestamp(serverTime, initialElapsedTime);
  const programLength = timestampEnd - timestampStart;
  const timePassed = currentTime - timestampStart;

  const percentageOfProgram = (timePassed / programLength) * 100;

  return percentageOfProgram >= 0 ? percentageOfProgram : 0;
};

/**
 * Simple progress percentage calculation based on clip elapsed time and total duration
 * @param {number} elapsedTime time difference from the start of the clip to the current state
 * @param {number} clipDuration total time duration of the present clip
 * @returns {number} percentage value if bigger than 0, otherwise 0
 */
export const getPercentageOfClipTime = (elapsedTime, clipDuration) => {
  const percentageOfClip = (elapsedTime / clipDuration) * 100;

  return percentageOfClip >= 0 ? percentageOfClip : 0;
};

export const withLeadingZero = value => `${value < 10 ? '0' : ''}${value}`;

/**
 * Formats timestamp into readable string hh:mm:ss
 * @param {number} timestamp current time in seconds
 * @returns {string} formatted time as hh:mm:ss
 */
export const getTimeFormat = timestamp => {
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  if (timestamp < 0) return '00:00';

  const hours = floorValue(secondsToHours(timestamp));
  const minutes = floorValue(secondsToMinutes(timestamp));
  const seconds = secondsRemainder(timestamp);

  if (timestamp >= 3600) {
    return `${hours}:${withLeadingZero(minutes)}:${withLeadingZero(seconds)}`;
  }

  return `${withLeadingZero(minutes)}:${withLeadingZero(seconds)}`;
};

const timeUnitsMultipliers = [1, 60, 3600];
export const timeTitleToSeconds = title =>
  title
    ? title
        .split(':')
        .reverse()
        .slice(0, timeUnitsMultipliers.length)
        .reduce((result, current, index) => result + parseInt(current, 10) * timeUnitsMultipliers[index], 0)
    : 0;

export const isInStartoverTimeSlot = ({
  timestampStartPreroll,
  timestampEndPreroll,
  timestampStart,
  timestampEnd,
  initialElapsedTime,
  serverTime,
  startoverMinDepth,
  startoverMaxDepth,
  startoverMinPercentage,
  startoverMaxPercentage,
}) => {
  const currentTime = getTimestamp(serverTime, initialElapsedTime);

  // Verify if we are still in preroll
  if (currentTime < timestampEndPreroll) {
    return false;
  }

  const programLength = timestampEnd - timestampStart;
  const lowestTimeAllowed = timestampStart + (programLength * startoverMinPercentage) / 100;
  const highestTimeAllowed = timestampStart + (programLength * startoverMaxPercentage) / 100;

  // is the clip long enough to be eligible
  if (differenceInMinutes(timestampEnd, timestampStart) < startoverMinDepth) {
    return false;
  }

  // is the max depth to start over reached already
  if (differenceInMinutes(currentTime, timestampStartPreroll) >= startoverMaxDepth) {
    return false;
  }

  // is it the good time in the clip to start over
  if (currentTime < lowestTimeAllowed || currentTime > highestTimeAllowed) {
    return false;
  }

  // we are in the startover established time slot
  return true;
};

export const clearTimeouts = timeoutIds => {
  timeoutIds.forEach(clearTimeout);
};

/**
 * Returns the elapsed time since the program startTime. If result is negative return 0.
 *
 * @param {number} currentTime timestamp in seconds
 * @param {number} startTime timestamp in seconds
 * @returns {number} seconds elapsed rounded
 */
export const videoElapsedTime = (currentTime, startTime) => Math.max(Math.round(currentTime - startTime), 0);

/**
 * Returns current time in seconds
 *
 * @returns {number}
 */
export const getCurrentTimeInSeconds = () => Math.floor(Date.now() / 1000);
